import * as THREE from 'heritage/assets/three.module.js';
import { buildRingStudy, ringReference } from 'heritage/ring-study.js';
import { createGoldMaterial } from 'heritage/gold-material.js';

const clamp=n=>Math.max(0,Math.min(1,n));
const smooth=(a,b,n)=>{const t=clamp((n-a)/(b-a));return t*t*(3-2*t);};
const lerp=(a,b,t)=>a+(b-a)*t;

// Preserve all making stages while traversing their timeline in half the scroll.
export const PROCESS_SCROLL_VH=640;
const PROCESS_TIMELINE_UNITS=1280;
const processBeats=[
  [0,0],[120,.18],[208,.18],[320,.34],[576,.34],
  [688,.52],[800,.735],[832,.735],
  [960,1],[1216,1],[1280,1]
];
export function sampleProcessScroll(scrollProgress){
  const travel=clamp(scrollProgress)*PROCESS_TIMELINE_UNITS;
  let progress=1;
  for(let i=1;i<processBeats.length;i++){
    const [end,to]=processBeats[i], [start,from]=processBeats[i-1];
    if(travel<=end){progress=lerp(from,to,(travel-start)/(end-start));break;}
  }
  const castTurn=.35*smooth(688,832,travel)*(1-smooth(832,960,travel));
  const turn=Math.PI*2*(smooth(320,576,travel))+castTurn;
  return {progress,turn};
}

// An illustrative solitaire for the making sequence, not production CAD.
export function createProcessModel(){
  const group=new THREE.Group();
  const object=new THREE.Group();group.add(object);
  object.name='solitaire-ring';object.userData.reference=ringReference;
  const metal=createGoldMaterial({polished:false});
  const wireMaterial=new THREE.LineBasicMaterial({color:0x343a40,transparent:true,opacity:1});
  const guideMaterial=new THREE.LineBasicMaterial({color:0x7c8187,transparent:true,opacity:.32});
  const metalParts=[],wires=[];
  const pour={level:{value:-1.20},time:{value:0},heat:{value:0},active:{value:0},inverse:{value:new THREE.Matrix4()}};

  // Fill in the ring's own coordinates so the liquid follows the rotating object.
  // The metal is fully reflective from the outset; only its filled region exists.
  function fillShader(material,emptyOutline=false){
    material.onBeforeCompile=shader=>{
      Object.assign(shader.uniforms,{uFillLevel:pour.level,uPourTime:pour.time,uPourHeat:pour.heat,uPourActive:pour.active,uPourInverse:pour.inverse});
      shader.vertexShader=shader.vertexShader.replace('#include <common>',`#include <common>
        uniform mat4 uPourInverse;
        varying vec3 vPourPosition;`)
        .replace('#include <begin_vertex>',`#include <begin_vertex>
        vPourPosition = (uPourInverse * modelMatrix * vec4(transformed, 1.0)).xyz;`);
      shader.fragmentShader=shader.fragmentShader.replace('#include <common>',`#include <common>
        uniform float uFillLevel;
        uniform float uPourTime;
        uniform float uPourHeat;
        uniform float uPourActive;
        varying vec3 vPourPosition;`)
        .replace('#include <clipping_planes_fragment>',`#include <clipping_planes_fragment>
        float surface = uFillLevel + uPourHeat * 0.018 * sin(vPourPosition.x * 13.0 + uPourTime * 2.6);
        ${emptyOutline?'if (uPourActive > 0.5 && vPourPosition.y < surface) discard;':'if (vPourPosition.y > surface) discard;'}`);
      if(!emptyOutline)shader.fragmentShader=shader.fragmentShader.replace('#include <emissivemap_fragment>',`#include <emissivemap_fragment>
        float liquidEdge = 1.0 - smoothstep(0.0, 0.085, surface - vPourPosition.y);
        totalEmissiveRadiance += vec3(1.0, 0.48, 0.06) * liquidEdge * uPourHeat * 0.85;`);
    };
    material.customProgramCacheKey=()=>emptyOutline?'unfilled-gold-outline-v1':'rising-gold-v1';
  }
  fillShader(metal);fillShader(wireMaterial,true);

  function part(geometry,position=new THREE.Vector3(),rotation=null,parent=object){
    const mesh=new THREE.Mesh(geometry,metal);mesh.position.copy(position);if(rotation)mesh.rotation.copy(rotation);parent.add(mesh);metalParts.push(mesh);
    const line=new THREE.LineSegments(new THREE.EdgesGeometry(geometry,1),wireMaterial);line.position.copy(mesh.position);line.quaternion.copy(mesh.quaternion);parent.add(line);wires.push({line,mesh,count:line.geometry.attributes.position.count});
    return mesh;
  }
  const V=(x,y,z)=>new THREE.Vector3(x,y,z);

  // Graphite strokes, slight retracing and hatching precede the dimensional CAD.
  const drawing=new THREE.Group();drawing.name='solitaire-drawing';object.add(drawing);const strokes=[];
  const pencilMaterials=[.82,.28,.17].map(opacity=>new THREE.LineBasicMaterial({color:0x292824,transparent:true,opacity,depthTest:false}));
  function pencil(points,weight=0,start=0,end=.15,offset=0){
    const sampled=[];
    for(let j=0;j<points.length-1;j++){
      const a=points[j],b=points[j+1],steps=Math.max(2,Math.ceil(a.distanceTo(b)/.014));
      for(let i=0;i<steps;i++){
        const t=i/steps,n=sampled.length;
        sampled.push(a.clone().lerp(b,t).add(V(Math.sin(n*1.43)*.002+offset,Math.sin(n*.73)*.002+offset,0)));
      }
    }
    sampled.push(points.at(-1).clone());
    const line=new THREE.Line(new THREE.BufferGeometry().setFromPoints(sampled),pencilMaterials[weight]);
    line.renderOrder=3;drawing.add(line);strokes.push({line,start,end,count:sampled.length});return line;
  }
  const gemMaterials=[0xf8fbff,0xd8e4ef,0xf0f5fa,0xaebdce].map(color=>new THREE.MeshPhysicalMaterial({color,metalness:0,roughness:.018,transmission:.45,thickness:.4,ior:2.42,clearcoat:1,envMapIntensity:2.5,side:THREE.DoubleSide,transparent:true}));
  const {settings,prongs}=buildRingStudy({object,part,pencil,wireMaterial,gemMaterials});

  const guides=new THREE.Group();object.add(guides);
  const box=new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(2.75,3.5,1.45)),guideMaterial);box.position.y=.27;guides.add(box);
  const axes=[];
  for(const y of [-1.12,0,1.38])axes.push(V(-1.6,y,0),V(1.6,y,0));
  axes.push(V(0,-1.5,0),V(0,2.2,0));
  const axisLines=new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(axes),guideMaterial);guides.add(axisLines);

  function update(p,time,fade=1,lift=.65,turn=0,openingReveal=1){
    const dimensional=smooth(.19,.27,p),casting=smooth(.46,.50,p),finish=smooth(.77,.92,p),seated=smooth(.81,.92,p),closed=smooth(.91,.985,p);
    const sketchFade=(1-smooth(.19,.25,p))*fade;
    drawing.visible=sketchFade>.001;
    pencilMaterials.forEach((m,i)=>m.opacity=[.82,.28,.17][i]*sketchFade);
    strokes.forEach(({line,start,end,count},i)=>line.geometry.setDrawRange(0,Math.floor(count*smooth(start-.025,end,p)*(i===0?1:smooth(.7,1,openingReveal)))));
    pour.time.value=time;pour.active.value=p>=.475?1:0;
    pour.level.value=lerp(-1.20,1.88,clamp((p-.475)/.25));
    pour.heat.value=smooth(.48,.52,p)*(1-smooth(.715,.76,p));
    metal.opacity=fade;metal.roughness=lerp(.18,.115,finish);metal.clearcoat=lerp(.65,.85,finish);
    metalParts.forEach(mesh=>mesh.visible=p>=.475&&fade>.002);
    // The remaining cavity stays outlined until the rising gold reaches it.
    wireMaterial.opacity=dimensional*lerp(.68,.16,casting)*(1-smooth(.725,.77,p))*fade;
    wireMaterial.color.setRGB(lerp(.035,.8,casting),lerp(.042,.52,casting),lerp(.05,.20,casting));
    guideMaterial.opacity=.27*dimensional*(1-smooth(.40,.47,p))*fade;
    guides.visible=guideMaterial.opacity>.001;
    for(const {stem,tip,open,closed:closedPosition} of prongs){
      stem.morphTargetInfluences[0]=closed;
      tip.position.copy(open).lerp(closedPosition,closed);
    }
    wires.forEach(({line,mesh,count},i)=>{
      line.position.copy(mesh.position);line.quaternion.copy(mesh.quaternion);line.scale.copy(mesh.scale);line.visible=wireMaterial.opacity>.002;
      const draw=smooth(.19,.33,p)*.75+.25;
      line.geometry.setDrawRange(0,Math.floor(count*clamp(draw*1.4-i/Math.max(1,wires.length-1)*.38)/2)*2);
    });
    for(const setting of settings){
      setting.gemWire.visible=dimensional>.001&&p<.47;
      setting.gem.visible=p>.78;
      setting.gem.position.y=(1-seated)*lift;
    }
    gemMaterials.forEach(m=>m.opacity=smooth(.78,.815,p)*fade);
    object.rotation.set(dimensional*.44,-.38*dimensional+turn,-.10);
    object.updateWorldMatrix(true,false);pour.inverse.value.copy(object.matrixWorld).invert();
  }
  group.visible=false;update(0,0);
  return {group,update};
}